import { HERO_COUNT, HeroId, Slot, type AbilityMap, type HeroDef } from "../types.ts";
import { pk, zk, ck, fxk, type BarrierSpec, type ConstructKind, type HeroModule } from "../module.ts";
import { setProjectileRegistry, type ProjectileKind, type ZoneKind } from "../kitlib.ts";
import h0 from "./captain-americano.ts";
import h1 from "./doctor-estranged.ts";
import h2 from "./root.ts";
import h3 from "./the-bulk.ts";
import h4 from "./magnetron.ts";
import h5 from "./penny-parka.ts";
import h6 from "./thud.ts";
import h7 from "./venison.ts";
import h8 from "./black-panthera.ts";
import h9 from "./black-window.ts";
import h10 from "./squawkeye.ts";
import h11 from "./hella.ts";
import h12 from "./irony-fist.ts";
import h13 from "./ion-man.ts";
import h14 from "./tragik.ts";
import h15 from "./noon-knight.ts";
import h16 from "./sub-marinara.ts";
import h17 from "./psyclone.ts";
import h18 from "./scarlet-twitch.ts";
import h19 from "./cider-man.ts";
import h20 from "./squirrely-girl.ts";
import h21 from "./star-lorde.ts";
import h22 from "./brainstorm.ts";
import h23 from "./the-punnisher.ts";
import h24 from "./winter-shoulder.ts";
import h25 from "./woolverine.ts";
import h26 from "./atom-warlock.ts";
import h27 from "./cloak-and-swagger.ts";
import h28 from "./geoff.ts";
import h29 from "./lowkey.ts";
import h30 from "./luna-snowcone.ts";
import h31 from "./mantissa.ts";
import h32 from "./pocket-raccoon.ts";

const MODULES: HeroModule[] = [h0, h1, h2, h3, h4, h5, h6, h7, h8, h9, h10, h11, h12, h13, h14, h15, h16, h17, h18, h19, h20, h21, h22, h23, h24, h25, h26, h27, h28, h29, h30, h31, h32];

function inRange(kind: number, base: number, count: number) {
  return kind >= base && kind < base + count;
}

for (let i = 0; i < MODULES.length; i++) {
  const m = MODULES[i]!;
  if (m.def.id !== i) throw new Error(`hero module order mismatch at ${i}`);
  for (const k of Object.keys(m.projectiles ?? {}).map(Number)) if (!inRange(k, pk(m.def.id, 0), 8)) throw new Error(`${m.def.slug} projectile kind ${k} outside range`);
  for (const k of Object.keys(m.zones ?? {}).map(Number)) if (!inRange(k, zk(m.def.id, 0), 4)) throw new Error(`${m.def.slug} zone kind ${k} outside range`);
  for (const k of Object.keys(m.constructs ?? {}).map(Number)) if (!inRange(k, ck(m.def.id, 0), 4)) throw new Error(`${m.def.slug} construct kind ${k} outside range`);
  for (const k of Object.keys(m.fx ?? {}).map(Number)) if (!inRange(k, fxk(m.def.id, 0), 8)) throw new Error(`${m.def.slug} fx kind ${k} outside range`);
}
if (MODULES.length !== HERO_COUNT) throw new Error(`expected ${HERO_COUNT} hero modules, found ${MODULES.length}`);

export const HERO_MODULES = MODULES as readonly HeroModule[];
export const HEROES = Object.fromEntries(MODULES.map((m) => [m.def.id, m.def])) as Record<HeroId, HeroDef>;
export const HERO_LIST = MODULES.map((m) => m.def);
export const KITS = Object.fromEntries(MODULES.map((m) => [m.def.id, m.kit])) as Record<HeroId, HeroModule["kit"]>;
export const TARGETS = Object.fromEntries(MODULES.map((m) => [m.def.id, m.targets ?? {}])) as Record<HeroId, NonNullable<HeroModule["targets"]>>;
export const PROJECTILES = Object.assign({}, ...MODULES.map((m) => m.projectiles ?? {})) as Record<number, ProjectileKind>;
export const ZONES = Object.assign({}, ...MODULES.map((m) => m.zones ?? {})) as Record<number, ZoneKind>;
export const CONSTRUCTS = Object.assign({}, ...MODULES.map((m) => m.constructs ?? {})) as Record<number, ConstructKind>;
export const FX = Object.assign({}, ...MODULES.map((m) => m.fx ?? {})) as Record<number, { color: string }>;
export const FX_COLORS = Object.fromEntries(Object.entries(FX).map(([k, v]) => [Number(k), v.color])) as Record<number, string>;
export const BARRIERS = Object.fromEntries(MODULES.filter((m) => m.barrier).map((m) => [m.def.id, m.barrier!])) as Partial<Record<HeroId, BarrierSpec>>;

setProjectileRegistry(PROJECTILES);

const FORM_CACHE = new Map<string, HeroDef>();

/** Returns the memoized hero definition for a hero's current form (form 0 = base def). */
export function heroDef(hero: HeroId, form = 0): HeroDef {
  const base = HEROES[hero];
  if (!base || form <= 0) return base;
  const key = `${hero}:${form}`;
  const cached = FORM_CACHE.get(key);
  if (cached) return cached;
  const over = base.forms?.[form - 1];
  if (!over) return base;
  const abilities: Partial<Record<Slot, unknown>> = { ...base.abilities };
  for (const [slot, info] of Object.entries(over.abilities ?? {}) as [string, NonNullable<typeof over.abilities>[Slot]][]) {
    const s = Number(slot) as Slot;
    if (info === null) delete abilities[s];
    else abilities[s] = info;
  }
  const merged: HeroDef = {
    ...base,
    ...over,
    id: base.id,
    slug: base.slug,
    role: base.role,
    blurb: base.blurb,
    forms: base.forms,
    abilities: abilities as AbilityMap,
  };
  FORM_CACHE.set(key, merged);
  return merged;
}

export const PK = {
  Dagger: pk(HeroId.DoctorEstranged, 0),
  RepairOrb: pk(HeroId.PocketRaccoon, 0),
} as const;
export const ZK = {
  Portal: zk(HeroId.DoctorEstranged, 0),
  Beacon: zk(HeroId.PocketRaccoon, 0),
  Amplifier: zk(HeroId.PocketRaccoon, 1),
} as const;
